import React, { useState, useEffect, useMemo } from "react";
import { Col, Container, Form, Row, Button, Table } from "react-bootstrap";
import { FieldArray, useFormikContext } from "formik";
import { BsExclamationCircleFill, BsPlusLg, BsTrash } from "react-icons/bs";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { getByNpmId, addEvent, editEvent, deleteEvent } from "../../../../HttpService/climateEventsService.ts";


const eventTypeOptions = [
  { value: "Flood", label: "Flood" },
  { value: "Landslide", label: "Landslide" },
  { value: "Sea surge", label: "Sea surge" },
  { value: "High temperature", label: "High temperature" },
  { value: "Snowdrift", label: "Snowdrift" },
];

const defaultRowValue = {
  id: null,
  npmId: null,
  routeId: "",
  eventType: "",
  startPointLat: "",
  startPointLon: "",
  endPointLat: "",
  endPointLon: "",
  disruptionStartDate: null,
  disruptionEndDate: null,
};

const TODIS_FORM_CLIMATE = (props) => {
  const { values, errors, touched, setFieldValue } = useFormikContext();
  const [showFullTable, setShowFullTable] = useState(false);
  const [loading, setLoading] = useState(false);
  const [savingIndex, setSavingIndex] = useState(null);
  const [deletingIndex, setDeletingIndex] = useState(null);

  const assetType = "railways";
  const dtoField = "railwaysNpmClimateDTO";

  const climateData = values[dtoField] || {};
  const climateEvents = useMemo(() => climateData.climateEvents || [], [climateData.climateEvents]);
  const climateErrors = errors[dtoField] || {};
  const climateTouched = touched[dtoField] || {};

  const pathFor = (index, field) => `${dtoField}.climateEvents[${index}].${field}`;

  useEffect(() => {
    const fetchClimateEvents = async () => {
      if (!values.npmRwId) return;
      setLoading(true);
      try {
        // getByNpmId now returns normalized events (contains npmId, startLatitude, startDate, etc.)
        const events = await getByNpmId(values.npmRwId, assetType);
        const formatted = (events || []).map((ev) => ({
          id: ev.id,
          npmId: ev.npmId,
          routeId: ev.routeId,
          eventType: ev.eventType,
          startPointLat: ev.startLatitude ?? "",
          startPointLon: ev.startLongitude ?? "",
          endPointLat: ev.endLatitude ?? "",
          endPointLon: ev.endLongitude ?? "",
          disruptionStartDate: ev.startDate ? new Date(ev.startDate) : null,
          disruptionEndDate: ev.endDate ? new Date(ev.endDate) : null,
        }));
        setFieldValue(`${dtoField}.climateEvents`, formatted);
      } catch (error) {
        console.error("Error loading climate events:", error);
        setFieldValue(`${dtoField}.climateEvents`, []);
      } finally {
        setLoading(false);
      }
    };
    fetchClimateEvents();
  }, [values.npmRwId, setFieldValue]);

  const handleAddRow = async (push) => {
    push({ ...defaultRowValue });
  };

  const handleRemoveRow = async (remove, index) => {
    const event = climateEvents[index];
    if (event && event.id) {
      setDeletingIndex(index);
      try {
        await deleteEvent(event.id, assetType);
      } catch (error) {
        console.error("Error deleting climate event:", error);
        alert("Failed to delete climate event. Please try again.");
        setDeletingIndex(null);
        return;
      }
      setDeletingIndex(null);
    }
    remove(index);
  };

  const handleEventTypeChange = (index, selectedValue) => {
    setFieldValue(pathFor(index, "eventType"), selectedValue);
  };

  const handleDateChange = (index, fieldName, date) => {
    setFieldValue(pathFor(index, fieldName), date);
  };

  const handleGpsChange = (index, fieldName, value) => {
    setFieldValue(pathFor(index, fieldName), value);
  };

  const handleSaveEvent = async (index) => {
    const event = climateEvents[index];
    if (
      !event.eventType ||
      event.startPointLat === "" ||
      event.startPointLon === "" ||
      event.endPointLat === "" ||
      event.endPointLon === "" ||
      !event.disruptionStartDate ||
      !event.disruptionEndDate
    ) {
      alert("Please fill in all required fields");
      return;
    }

    setSavingIndex(index);
    try {
      const payload = {
        id: event.id ?? undefined,
        npmId: props.values?.npmRwId ?? event.npmId,
        routeId: props.values?.routeId || event.routeId || "",
        eventType: event.eventType ? String(event.eventType).toLowerCase() : "",
        startDate: event.disruptionStartDate ? event.disruptionStartDate.toISOString().split("T")[0] : null,
        endDate: event.disruptionEndDate ? event.disruptionEndDate.toISOString().split("T")[0] : null,
        startLatitude: parseFloat(event.startPointLat),
        startLongitude: parseFloat(event.startPointLon),
        endLatitude: parseFloat(event.endPointLat),
        endLongitude: parseFloat(event.endPointLon),
      };

      let response;
      if (event.id) {
        response = await editEvent(payload, assetType);
      } else {
        response = await addEvent(payload, assetType);
        const returnedId = response?.data?.id ?? response?.id ?? null;
        if (returnedId) {
          setFieldValue(pathFor(index, "id"), returnedId);
        }
      }
    } catch (error) {
      console.error("Error saving climate event:", error);
      alert(`Failed to save climate event: ${error?.message ?? error}`);
    } finally {
      setSavingIndex(null);
    }
  };

  return (
    <Container>
      <Row>
        <Col lg={12}>
          <h5 className="mt-4 mb-3">Climate Resilience Events (railways)</h5>
          <FieldArray
            name={`${dtoField}.climateEvents`}
            render={(arrayHelpers) => (
              <div>
                {loading ? (
                  <div className="alert alert-info">Loading climate resilience events...</div>
                ) : climateEvents && climateEvents.length > 0 ? (
                  <>
                    <div className="d-flex justify-content-end mb-2">
                      <Button variant="outline-secondary" size="sm" onClick={() => setShowFullTable((prev) => !prev)} disabled={props.disabled}>
                        {showFullTable ? "Show less" : "Show more"}
                      </Button>
                    </div>

                    <div className="table-responsive" style={{ maxWidth: "100%", overflowX: showFullTable ? "auto" : "hidden" }}>
                      <Table striped bordered hover size="sm">
                        <thead>
                          <tr>
                            <th>Event Type</th>
                            <th>Start Point Latitude</th>
                            <th>Start Point Longitude</th>
                            <th>End Point Latitude</th>
                            <th>End Point Longitude</th>
                            {showFullTable && (
                              <>
                                <th>Disruption Start Date</th>
                                <th>Disruption End Date</th>
                              </>
                            )}
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {climateEvents.map((event, index) => (
                            <tr key={index}>
                              <td>
                                <Form.Select
                                  name={pathFor(index, "eventType")}
                                  value={event.eventType || ""}
                                  onChange={(e) => handleEventTypeChange(index, e.target.value)}
                                  disabled={props.disabled}
                                  size="sm"
                                  className={climateErrors.climateEvents && climateErrors.climateEvents[index]?.eventType ? "is-invalid" : ""}
                                >
                                  <option value="">Select Event Type</option>
                                  {eventTypeOptions.map((opt) => (
                                    <option key={opt.value} value={opt.value}>
                                      {opt.label}
                                    </option>
                                  ))}
                                </Form.Select>
                                {climateErrors.climateEvents && climateErrors.climateEvents[index]?.eventType && climateTouched.climateEvents && climateTouched.climateEvents[index]?.eventType && (
                                  <div className="todis-field-validation-err" style={{ fontSize: "0.75rem" }}>
                                    <span className="todis-field-validation-err-icon">
                                      <BsExclamationCircleFill size={12} />
                                    </span>
                                    {climateErrors.climateEvents[index].eventType}
                                  </div>
                                )}
                              </td>

                              <td>
                                <Form.Control type="number" step="0.00001" placeholder="Latitude" value={event.startPointLat ?? ""} onChange={(e) => handleGpsChange(index, "startPointLat", e.target.value)} disabled={props.disabled} size="sm" />
                              </td>

                              <td>
                                <Form.Control type="number" step="0.00001" placeholder="Longitude" value={event.startPointLon ?? ""} onChange={(e) => handleGpsChange(index, "startPointLon", e.target.value)} disabled={props.disabled} size="sm" />
                              </td>

                              <td>
                                <Form.Control type="number" step="0.00001" placeholder="Latitude" value={event.endPointLat ?? ""} onChange={(e) => handleGpsChange(index, "endPointLat", e.target.value)} disabled={props.disabled} size="sm" />
                              </td>

                              <td>
                                <Form.Control type="number" step="0.00001" placeholder="Longitude" value={event.endPointLon ?? ""} onChange={(e) => handleGpsChange(index, "endPointLon", e.target.value)} disabled={props.disabled} size="sm" />
                              </td>

                              {showFullTable && (
                                <>
                                  <td>
                                    <DatePicker selected={event.disruptionStartDate ? new Date(event.disruptionStartDate) : null} onChange={(date) => handleDateChange(index, "disruptionStartDate", date)} dateFormat="yyyy-MM-dd" placeholderText="Start Date" disabled={props.disabled} className={`form-control form-control-sm ${climateErrors.climateEvents && climateErrors.climateEvents[index]?.disruptionStartDate ? "is-invalid" : ""}`} />
                                  </td>
                                  <td>
                                    <DatePicker selected={event.disruptionEndDate ? new Date(event.disruptionEndDate) : null} onChange={(date) => handleDateChange(index, "disruptionEndDate", date)} dateFormat="yyyy-MM-dd" placeholderText="End Date" disabled={props.disabled} className={`form-control form-control-sm ${climateErrors.climateEvents && climateErrors.climateEvents[index]?.disruptionEndDate ? "is-invalid" : ""}`} />
                                  </td>
                                </>
                              )}

                              <td className="text-center">
                                <div className="d-flex gap-2 justify-content-center">
                                  <Button variant="success" size="sm" onClick={() => handleSaveEvent(index)} disabled={props.disabled || savingIndex === index}>
                                    {savingIndex === index ? "Saving..." : event.id ? "Update" : "Save"}
                                  </Button>
                                  <Button variant="danger" size="sm" onClick={() => handleRemoveRow(arrayHelpers.remove, index)} disabled={props.disabled || deletingIndex === index}>
                                    {deletingIndex === index ? "Deleting..." : <BsTrash size={16} />}
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  </>
                ) : (
                  <div className="alert alert-info">No climate resilience events added yet.</div>
                )}
                <Button variant="primary" size="sm" onClick={() => handleAddRow(arrayHelpers.push)} disabled={props.disabled} className="mt-3">
                  <BsPlusLg /> Add Climate Event
                </Button>
              </div>
            )}
          />
        </Col>
      </Row>
    </Container>
  );
};

export default TODIS_FORM_CLIMATE;